import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarShares } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { PersonPicker } from '../../components/person-picker/PersonPicker';
import { useSession } from '../../session';
import { useAssignees } from './assignees';

/** Hier steht, wer den eigenen Kalender sieht. Die Liste liegt auf dem Server und gilt für alle Termine, an denen man teilnimmt. */
export function useCalendarShares() {
  return useQuery({ queryKey: ['calendar-shares'], queryFn: async () => (await api<{ data: CalendarShares }>('GET', '/calendar-shares')).data });
}

/**
 * Bereich „Kalender freigeben“ (KON-072). Die gewählten Kollegen sehen die eigenen Termine mit Uhrzeit und Titel, aber ohne Beschreibung, Ort, Art und Teilnehmer,
 * und können sie nicht öffnen. Gespeichert wird die ganze Liste, eine leere Liste nimmt die Freigabe zurück.
 */
export function ShareCalendarPanel({ onClose }: { onClose: () => void }) {
  const { session } = useSession();
  const client = useQueryClient();
  const assignees = useAssignees();
  const shares = useCalendarShares();
  const [selected, setSelected] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (shares.data && !loaded) {
      setSelected(shares.data.sharedWith);
      setLoaded(true);
    }
  }, [shares.data, loaded]);

  const save = useMutation({
    mutationFn: () => api<{ data: CalendarShares }>('PUT', '/calendar-shares', { userIds: selected }),
    onSuccess: (res) => {
      client.setQueryData(['calendar-shares'], res.data);
      client.invalidateQueries({ queryKey: ['appointments'] });
      setMessage({ ok: true, text: selected.length === 0 ? 'Die Freigabe wurde zurückgenommen.' : `Ihr Kalender ist für ${selected.length === 1 ? '1 Kollegen' : `${selected.length} Kollegen`} freigegeben.` });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? (e.fields.userIds ?? e.message) : 'Die Freigabe konnte nicht gespeichert werden.' }),
  });

  const unchanged = shares.data && JSON.stringify([...selected].sort()) === JSON.stringify(shares.data.sharedWith);

  return (
    <section className="card share-panel" aria-label="Kalender freigeben">
      <div className="page-head">
        <h2>Kalender freigeben</h2>
        <button type="button" className="btn btn--secondary btn--sm page-head__action" onClick={onClose}>Schließen</button>
      </div>
      <p className="muted small">
        Die Kollegen, die Sie hier auswählen, sehen in ihrem Kalender alle Ihre Termine mit Uhrzeit und Titel. Beschreibung, Ort, Art und Teilnehmer bleiben verborgen, und die Termine lassen sich nicht öffnen.
        Sie können die Freigabe jederzeit zurücknehmen.
      </p>
      {shares.isLoading && <p className="muted">Wird geladen …</p>}
      {shares.data && (
        <>
          <PersonPicker label="Freigegeben für" options={assignees.list.filter((a) => a.id !== session?.user.id)} value={selected} onChange={(ids) => { setSelected(ids); setMessage(null); }} placeholder="Kollegen suchen …" />
          {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn--primary" disabled={save.isPending || !!unchanged} onClick={() => save.mutate()}>
              {save.isPending ? 'Wird gespeichert …' : 'Freigabe speichern'}
            </button>
          </div>
          {shares.data.sharedWithMe.length > 0 && (
            <p className="muted small">Für Sie freigegeben haben: {shares.data.sharedWithMe.map((s) => s.name).join(', ')}.</p>
          )}
        </>
      )}
    </section>
  );
}
